import React from 'react';
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace';
import { addDays, format, isEqual } from 'date-fns';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import Box from '@mui/material/Box';
import { blueGrey, green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  DateRange,
  RequestPass
} from '../../models';

import {
  PassStatusID
} from '../../global';


interface PassesMobileProps {
  dateRange: DateRange;
  days: number;
  isLoading: boolean;
  rows: Array<Array<RequestPass>>;
}

const PassesMobile: React.FC<PassesMobileProps> = (props) => {

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      margin: theme.spacing(1, 0, 0, 0),
    },
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0)
    },
    passDate: {
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(1, 0, 0, 0),
      padding: theme.spacing(1, 0, 1, 0)
    },
    pending: {
      color: blueGrey[500]
    }
  };

  // #endregion

  // #region Methods

  /*
  * Render passes for the date range
  */
  const renderPasses = () => {

    let a: Array<ReactJSXElement> = [];

    if (props.rows.length === 0) {
      a.push(<Typography>No passes for the selected dates</Typography>);
    } else {
      const start = new Date(props.dateRange.startDate);

      // For each date in range
      for (var i = 0; i < props.days; i++) {
        const date = addDays(start, i);

        // Add date header
        a.push(<Typography sx={styles.passDate}>{format(date, 'EEE M/d/y')}</Typography>);

        const b: Array<ReactJSXElement> = [];
        // Build array of passes for the date
        const c = props.rows.flatMap(p => p.filter(r => isEqual(new Date(r.passDate), date)));

        if (c.length > 0) {
          c.forEach(p => {
            b.push(<Typography>{p.rank}-{p.person} {renderStatus(p.statusID, p.status)}</Typography>);
          });
        }
        if (b.length > 0) { 
          a = [...a, ...b];
        } else {
          a.push(<Typography>No passes requested</Typography>);
        }

      }

      return a;

    }

  }

  /*
   * Render the request status icon
   */
  const renderStatus = (statusID: number, status: string) => {

    switch (statusID) {
      case PassStatusID.Requested:
        return (
          <Tooltip title={status} arrow>
            <PendingIcon fontSize="medium" sx={styles.pending} />
          </Tooltip>
        )
      case PassStatusID.Confirmed:
        return (
          <Tooltip title={status} arrow>
            <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
          </Tooltip>
        )
      case PassStatusID.Denied:
        return (
          <Tooltip title={status} arrow>
            <BlockIcon fontSize="medium" sx={styles.deined} />
          </Tooltip>
        )
      default:
        return (<span />)
    }

  }

  // #endregion

  return (
    <Box sx={styles.root}>
      {renderPasses()}
    </Box>
  );

}

export default PassesMobile;